import { Check, X } from 'lucide-react'
import { useStudioLocale } from '@/i18n'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'
import { useStudioStore } from '@/store/studio-store'

interface OnboardingTourCopy {
  title: string
  description: string
  steps: readonly [string, string, string]
  skipTour: string
  skip: string
  done: string
}

const ONBOARDING_TOUR_COPY: Record<LocaleCode, OnboardingTourCopy> = {
  en: {
    title: 'Studio tour',
    description: 'Start with the app, refine a screen, then adjust behavior before publishing.',
    steps: ['App', 'Screen', 'Behavior'],
    skipTour: 'Skip tour',
    skip: 'Skip',
    done: 'Done',
  },
  ko: {
    title: 'Studio 둘러보기',
    description: '앱에서 시작해 화면을 다듬고, 발행하기 전에 동작을 조정하세요.',
    steps: ['앱', '화면', '동작'],
    skipTour: '둘러보기 건너뛰기',
    skip: '건너뛰기',
    done: '완료',
  },
  ja: {
    title: 'Studio ツアー',
    description: 'アプリから始め、画面を整え、公開前にアクションを調整します。',
    steps: ['アプリ', '画面', 'アクション'],
    skipTour: 'ツアーをスキップ',
    skip: 'スキップ',
    done: '完了',
  },
  zh: {
    title: 'Studio 导览',
    description: '从应用开始，完善页面，并在发布前调整动作。',
    steps: ['应用', '页面', '动作'],
    skipTour: '跳过导览',
    skip: '跳过',
    done: '完成',
  },
  'zh-TW': {
    title: 'Studio 導覽',
    description: '從應用開始，完善頁面，並在發佈前調整動作。',
    steps: ['應用', '頁面', '動作'],
    skipTour: '略過導覽',
    skip: '略過',
    done: '完成',
  },
}

export function OnboardingTour({
  locale: localeOverride,
}: {
  locale?: LocaleCode
} = {}) {
  const { locale: activeLocale } = useStudioLocale()
  const locale = localeOverride ?? activeLocale
  const copy = ONBOARDING_TOUR_COPY[locale]
  const status = useStudioStore((s) => s.onboardingStatus)
  const complete = useStudioStore((s) => s.completeOnboarding)
  const skip = useStudioStore((s) => s.skipOnboarding)

  if (status !== 'pending') return null

  return (
    <div
      className="pointer-events-none absolute inset-0 z-40 flex items-start justify-center bg-background/45 px-4 pt-14 backdrop-blur-[1px]"
      data-testid="onboarding-tour"
    >
      <section
        aria-describedby="studio-tour-description"
        aria-labelledby="studio-tour-title"
        aria-modal="true"
        className="pointer-events-auto w-full max-w-md rounded-lg border border-border bg-card p-4 shadow-lg"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground" id="studio-tour-title">
              {copy.title}
            </h2>
            <p className="mt-1 text-xs leading-5 text-muted-foreground" id="studio-tour-description">
              {copy.description}
            </p>
          </div>
          <button
            type="button"
            aria-label={copy.skipTour}
            onClick={skip}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        <ol className="mt-4 grid gap-2">
          {copy.steps.map((step, index) => (
            <li
              key={step}
              className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded border border-border text-[10px] text-muted-foreground">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={skip}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
          >
            {copy.skip}
          </button>
          <button
            type="button"
            onClick={complete}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:bg-foreground/90"
          >
            <Check size={13} aria-hidden />
            {copy.done}
          </button>
        </div>
      </section>
    </div>
  )
}
